/**
 * Recurring Transactions Management Page
 *
 * Allows Finance Managers to create, view, edit, and delete recurring transaction templates.
 * Preparers have read-only access.
 *
 * Demo mode: uses in-memory demo data.
 * Live mode: calls backend API endpoints.
 */

import { format } from "date-fns";
import { ChevronDown, Eye, Info, Pencil, Plus, Trash2 } from "lucide-react";
import { cn, formatDateOnlyLocal } from "../../lib/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../api/client";
import type {
	CreateRecurringTransactionRequestApi,
	RecurringTransactionApi,
	UpdateRecurringTransactionRequestApi,
} from "../../api/models/recurringTransaction";
import type { RecurringTransactionViewFilterInput } from "../../api/models/recurringTransactionView";
import {
	createRecurringTransaction,
	deleteRecurringTransaction,
	getAllRecurringTransactions,
	updateRecurringTransaction,
} from "../../api/services/recurringTransactionService";
import {
	type RecurringTransactionViewSortInput,
	getRecurringTransactionsCount,
	getRecurringTransactionsGraphql,
} from "../../api/services/recurringTransactionGraphqlService";
import { useApp } from "../../contexts/AppContext";
import { getRecurringTransactions } from "../../data/recurring-transactions-data";
import { useToast } from "../../hooks/use-toast";
import { useExportToExcel } from "../../hooks/useExportToExcel";
import { useFormatting } from "../../hooks/useFormatting";
import { usePermissions } from "../../hooks/usePermissions";
import { useScreenColumnPreferences, type DefaultColumn } from "../../hooks/useScreenColumnPreferences";
import { SCREEN_NAMES } from "../../constants/screenNames";
import type {
	RecurringFrequency,
	RecurringTransaction,
	RecurringTransactionFormData,
	RecurringTransactionStatus,
} from "../../types/recurring-transaction";
import { type ColumnConfig, type ColumnFilters, MaximusTableReusable } from "../MaximusTableReusable";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { RecurringTransactionModal } from "./RecurringTransactionModal";

// ─── Frequency conversion helpers ────────────────────────────────────────────

function toUiFrequency(frequency: string, frequencyNumber: number): RecurringFrequency {
	const key = `${frequency.toLowerCase()}:${frequencyNumber}`;
	const map: Record<string, RecurringFrequency> = {
		"daily:1": "Daily",
		"weekly:1": "Weekly",
		"weekly:2": "Fortnightly",
		"monthly:1": "Monthly",
		"monthly:3": "Quarterly",
		"yearly:1": "Annual",
	};
	return map[key] ?? "Monthly";
}

function fromUiFrequency(uiFrequency: RecurringFrequency): { frequency: string; frequencyNumber: number } {
	const map: Record<RecurringFrequency, { frequency: string; frequencyNumber: number }> = {
		Daily: { frequency: "daily", frequencyNumber: 1 },
		Weekly: { frequency: "weekly", frequencyNumber: 1 },
		Fortnightly: { frequency: "weekly", frequencyNumber: 2 },
		Monthly: { frequency: "monthly", frequencyNumber: 1 },
		Quarterly: { frequency: "monthly", frequencyNumber: 3 },
		Annual: { frequency: "yearly", frequencyNumber: 1 },
	};
	return map[uiFrequency];
}

// ─── API / Frontend mappers ──────────────────────────────────────────────────

function mapApiToFrontend(api: RecurringTransactionApi): RecurringTransaction {
	return {
		id: String(api.id),
		name: api.name,
		direction: api.direction as "inflow" | "outflow",
		amount: api.isVariable ? null : api.amount,
		isVariable: api.isVariable,
		currency: api.currency,
		frequency: toUiFrequency(api.frequency, api.frequencyNumber),
		startDate: new Date(api.startDate),
		endDate: api.endDate ? new Date(api.endDate) : undefined,
		nextDueDate: new Date(api.nextDueDate),
		category: api.category,
		counterpartyName: api.counterpartyName ?? undefined,
		status: api.status as RecurringTransactionStatus,
		notes: api.notes ?? undefined,
		source: api.source,
		createdAt: new Date(api.dateCreated),
		updatedAt: new Date(api.dateModified),
		dataSourceName:
			api.source === "manual"
				? "Manual Entry - Recurring Transactions"
				: api.source,
	};
}

function formatDateForApi(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function calculateNextDueDate(
	startDate: Date,
	freq: RecurringFrequency,
): Date {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const next = new Date(startDate);
	const { frequency, frequencyNumber } = fromUiFrequency(freq);

	while (next <= today) {
		switch (frequency) {
			case "daily":
				next.setDate(next.getDate() + frequencyNumber);
				break;
			case "weekly":
				next.setDate(next.getDate() + 7 * frequencyNumber);
				break;
			case "monthly":
				next.setMonth(next.getMonth() + frequencyNumber);
				break;
			case "yearly":
				next.setFullYear(next.getFullYear() + frequencyNumber);
				break;
		}
	}

	return next;
}

function mapFormToCreateRequest(
	formData: RecurringTransactionFormData,
): CreateRecurringTransactionRequestApi {
	const startDate = new Date(formData.startDate);
	const nextDueDate = calculateNextDueDate(startDate, formData.frequency);
	const { frequency, frequencyNumber } = fromUiFrequency(formData.frequency);

	return {
		name: formData.name.trim(),
		direction: formData.direction ?? "outflow",
		amount: formData.isVariable ? null : parseFloat(formData.amount),
		isVariable: formData.isVariable,
		currency: formData.currency,
		category: formData.category,
		counterpartyName: formData.counterpartyName?.trim() || undefined,
		frequency,
		frequencyNumber,
		startDate: formData.startDate,
		endDate: formData.endDate || undefined,
		nextDueDate: formatDateForApi(nextDueDate),
		status: formData.status,
		notes: formData.notes?.trim() || undefined,
	};
}

function mapFormToUpdateRequest(
	formData: RecurringTransactionFormData,
): UpdateRecurringTransactionRequestApi {
	const startDate = new Date(formData.startDate);
	const nextDueDate = calculateNextDueDate(startDate, formData.frequency);
	const { frequency, frequencyNumber } = fromUiFrequency(formData.frequency);

	return {
		name: formData.name.trim(),
		direction: formData.direction ?? "outflow",
		amount: formData.isVariable ? null : parseFloat(formData.amount),
		isVariable: formData.isVariable,
		currency: formData.currency,
		category: formData.category,
		counterpartyName: formData.counterpartyName?.trim() || undefined,
		frequency,
		frequencyNumber,
		startDate: formData.startDate,
		endDate: formData.endDate || undefined,
		nextDueDate: formatDateForApi(nextDueDate),
		status: formData.status,
		notes: formData.notes?.trim() || undefined,
	};
}

// ─── Server-side field maps ──────────────────────────────────────────────────

const SORT_FIELD_MAP: Record<string, string> = {
	name: "Name",
	amount: "Amount",
	frequency: "Frequency",
	nextDueDate: "NextDueDate",
	category: "Category",
	status: "Status",
};

const FILTER_FIELD_MAP: Record<string, string> = {
	name: "name",
	amount: "amount",
	frequency: "frequency",
	nextDueDate: "nextDueDate",
	category: "category",
	status: "status",
};

const TEXT_FILTER_FIELDS = new Set([
	"name",
	"frequency",
	"category",
	"status",
]);

const DATE_FILTER_FIELDS = new Set(["nextDueDate"]);

const NUMBER_FILTER_FIELDS = new Set(["amount"]);

function buildApiFilter(
	columnFilters: ColumnFilters,
): RecurringTransactionViewFilterInput | undefined {
	const entries = Object.entries(columnFilters);
	if (entries.length === 0) return undefined;

	const filterInput: RecurringTransactionViewFilterInput = {};

	for (const [columnKey, filterValue] of entries) {
		const apiField = FILTER_FIELD_MAP[columnKey];
		if (!apiField) continue;

		if (TEXT_FILTER_FIELDS.has(apiField)) {
			if (filterValue.value != null && filterValue.value !== "") {
				const strValue = String(filterValue.value);
				if (filterValue.mode === "equals") {
					(filterInput as Record<string, unknown>)[apiField] = {
						eq: strValue,
					};
				} else {
					(filterInput as Record<string, unknown>)[apiField] = {
						contains: strValue,
					};
				}
			}
		} else if (DATE_FILTER_FIELDS.has(apiField)) {
			const dateFilter: Record<string, string> = {};
			if (filterValue.dateFrom) {
				dateFilter.ge = formatDateOnlyLocal(filterValue.dateFrom);
			}
			if (filterValue.dateTo) {
				dateFilter.le = formatDateOnlyLocal(filterValue.dateTo);
			}
			if (Object.keys(dateFilter).length > 0) {
				(filterInput as Record<string, unknown>)[apiField] = dateFilter;
			}
		} else if (NUMBER_FILTER_FIELDS.has(apiField)) {
			const numFilter: Record<string, number> = {};
			if (filterValue.numberFrom != null) {
				numFilter.ge = filterValue.numberFrom;
			}
			if (filterValue.numberTo != null) {
				numFilter.le = filterValue.numberTo;
			}
			if (Object.keys(numFilter).length > 0) {
				(filterInput as Record<string, unknown>)[apiField] = numFilter;
			}
		}
	}

	return Object.keys(filterInput).length > 0 ? filterInput : undefined;
}

// ─── Status badge variant ─────────────────────────────────────────────────────

function getStatusVariant(status: RecurringTransactionStatus) {
	switch (status) {
		case "Active": return "success" as const;
		case "Paused": return "warning" as const;
		case "Ended": return "secondary" as const;
		default: return "secondary" as const;
	}
}

// ─── Export column mapping ───────────────────────────────────────────────────

const EXPORT_COLUMN_MAP: Record<string, { name: string; displayName: string }> = {
	name: { name: "name", displayName: "Name" },
	amount: { name: "amount", displayName: "Amount" },
	frequency: { name: "frequency", displayName: "Frequency" },
	nextDueDate: { name: "nextduedate", displayName: "Next Due Date" },
	category: { name: "category", displayName: "Category" },
	status: { name: "status", displayName: "Status" },
};

// ─── Component ───────────────────────────────────────────────────────────────

const DEFAULT_RECURRING_COLUMNS: DefaultColumn[] = [
	{ key: "name", defaultVisible: true, defaultOrder: 0 },
	{ key: "amount", defaultVisible: true, defaultOrder: 1 },
	{ key: "frequency", defaultVisible: true, defaultOrder: 2 },
	{ key: "nextDueDate", defaultVisible: true, defaultOrder: 3 },
	{ key: "category", defaultVisible: true, defaultOrder: 4 },
	{ key: "status", defaultVisible: true, defaultOrder: 5 },
	{ key: "id", defaultVisible: true, defaultOrder: 6 },
];

export function RecurringTransactionsPage() {
	const { hasPermission } = usePermissions();
	const { formatCurrency } = useFormatting();
	const { mode } = useApp();
	const { toast } = useToast();
	const { visibleColumns: savedVisibleColumns, savePreferences: saveColumnPreferences } = useScreenColumnPreferences(
		SCREEN_NAMES.RECURRING_TRANSACTIONS,
		DEFAULT_RECURRING_COLUMNS,
	);

	const [transactions, setTransactions] = useState<RecurringTransaction[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [bannerExpanded, setBannerExpanded] = useState(false);
	const [editingTransaction, setEditingTransaction] =
		useState<RecurringTransaction | null>(null);
	const [deletingTransaction, setDeletingTransaction] =
		useState<RecurringTransaction | null>(null);
	const [viewingTransaction, setViewingTransaction] =
		useState<RecurringTransaction | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Server-side state (live mode only)
	const [serverSearchTerm, setServerSearchTerm] = useState<string>("");
	const [sortParams, setSortParams] = useState<
		RecurringTransactionViewSortInput[] | undefined
	>(undefined);
	const [columnFilterParams, setColumnFilterParams] = useState<
		RecurringTransactionViewFilterInput | undefined
	>(undefined);
	const [serverPage, setServerPage] = useState(1);
	const [serverPageSize, setServerPageSize] = useState(25);
	const [pageCursors, setPageCursors] = useState<Record<number, string>>({});
	const [serverTotalCount, setServerTotalCount] = useState(0);

	const canEdit = hasPermission("manage_recurring_transactions");

	const getAllExportColumns = useCallback(() => {
		return Object.values(EXPORT_COLUMN_MAP).map((col) => ({
			name: col.name,
			displayName: col.displayName,
		}));
	}, []);

	const { triggerExport } = useExportToExcel({
		form: "RecurringTransactions",
		getColumns: getAllExportColumns,
		fileName: "recurring-transactions-export",
		onFinished: () =>
			toast({
				title: "Export complete",
				description: "Your file has been downloaded.",
			}),
		onError: (msg) =>
			toast({
				title: "Export failed",
				description: msg,
				variant: "destructive",
			}),
	});

	const loadTransactions = useCallback(async (
		sort?: RecurringTransactionViewSortInput[],
		first?: number,
		after?: string,
		colFilter?: RecurringTransactionViewFilterInput,
	) => {
		setIsLoading(true);
		setError(null);

		try {
			if (mode === "live") {
				const result = await getRecurringTransactionsGraphql(
					colFilter,
					first ?? serverPageSize,
					after,
					sort,
				);
				const mapped = result.edges.map((edge) => {
					const t = edge.node;
					return mapApiToFrontend({
						id: t.id,
						name: t.name,
						direction: t.direction ?? "outflow",
						amount: t.amount ?? 0,
						isVariable: t.isVariable,
						currency: t.currency ?? "GBP",
						category: t.category ?? "Uncategorised",
						counterpartyName: t.counterpartyName ?? null,
						frequency: t.frequency ?? "monthly",
						frequencyNumber: t.frequencyNumber,
						startDate: t.startDate,
						endDate: t.endDate ?? null,
						nextDueDate: t.nextDueDate,
						status: t.status ?? "Active",
						notes: t.notes ?? null,
						source: t.source ?? "manual",
						sourceId: null,
						externalId: null,
						payrollRunId: null,
						payrollEventType: null,
						createdByUserId: null,
						dataSourceId: null,
						dataSourceName: t.dataSourceName ?? null,
						tenantId: 0,
						dateCreated: new Date().toISOString(),
						dateModified: new Date().toISOString(),
					} as RecurringTransactionApi);
				});
				setTransactions(mapped);

				if (result.pageInfo.endCursor) {
					setPageCursors((prev) => ({
						...prev,
						[serverPage + 1]: result.pageInfo.endCursor as string,
					}));
				}

				const count = await getRecurringTransactionsCount(colFilter);
				setServerTotalCount(count);
			} else {
				setTransactions(getRecurringTransactions());
			}
		} catch (err) {
			const message =
				err instanceof ApiError
					? err.message
					: "Failed to load recurring transactions";
			setError(message);
			setTransactions([]);
		} finally {
			setIsLoading(false);
		}
	}, [mode, serverPageSize, serverPage]);

	useEffect(() => {
		if (mode === "live") {
			const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
			const searchFilter: RecurringTransactionViewFilterInput | undefined =
				serverSearchTerm.trim()
					? { summary: serverSearchTerm.trim() }
					: undefined;
			loadTransactions(sortParams, serverPageSize, cursor, { ...columnFilterParams, ...searchFilter });
		} else {
			loadTransactions();
		}
	}, [mode, sortParams, serverPage, serverPageSize, columnFilterParams, serverSearchTerm]);

	const handleSearchChange = useCallback((query: string) => {
		setServerSearchTerm(query);
		setServerPage(1);
		setPageCursors({});
	}, []);

	const handleColumnFilterChange = useCallback((filters: ColumnFilters) => {
		const apiFilter = buildApiFilter(filters);
		setColumnFilterParams(apiFilter);
		setServerPage(1);
		setPageCursors({});
	}, []);

	const handleSortChange = useCallback(
		(column: string | null, direction: "asc" | "desc" | null) => {
			if (!column || !direction) {
				setSortParams(undefined);
			} else {
				const field = SORT_FIELD_MAP[column];
				if (!field) {
					setSortParams(undefined);
				} else {
					setSortParams([
						{
							field,
							order: direction === "asc" ? "Ascending" : "Descending",
						},
					]);
				}
			}
			setServerPage(1);
			setPageCursors({});
		},
		[],
	);

	const handleAdd = () => {
		setEditingTransaction(null);
		setIsModalOpen(true);
	};

	const handleEdit = (transaction: RecurringTransaction) => {
		setEditingTransaction(transaction);
		setIsModalOpen(true);
	};

	const handleDelete = (transaction: RecurringTransaction) => {
		setDeletingTransaction(transaction);
	};

	const confirmDelete = async () => {
		if (!deletingTransaction) return;

		if (mode === "live") {
			setIsDeleting(true);
			try {
				await deleteRecurringTransaction(Number(deletingTransaction.id));
				toast({
					title: "Deleted",
					description: `"${deletingTransaction.name}" has been deleted.`,
				});
				setDeletingTransaction(null);
				if (mode === "live") {
					const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
					await loadTransactions(sortParams, serverPageSize, cursor, columnFilterParams);
				} else {
					await loadTransactions();
				}
			} catch (err) {
				const message =
					err instanceof ApiError
						? err.message
						: "Failed to delete recurring transaction";
				toast({ title: "Error", description: message, variant: "destructive" });
			} finally {
				setIsDeleting(false);
			}
		} else {
			setTransactions((prev) =>
				prev.filter((t) => t.id !== deletingTransaction.id),
			);
			setDeletingTransaction(null);
		}
	};

	const handleSave = async (
		transaction: RecurringTransaction,
		formData?: RecurringTransactionFormData,
	) => {
		if (mode === "live" && formData) {
			setIsSaving(true);
			try {
				if (editingTransaction) {
					const request = mapFormToUpdateRequest(formData);
					await updateRecurringTransaction(
						Number(editingTransaction.id),
						request,
					);
					toast({
						title: "Updated",
						description: `"${formData.name}" has been updated.`,
					});
				} else {
					const request = mapFormToCreateRequest(formData);
					await createRecurringTransaction(request);
					toast({
						title: "Created",
						description: `"${formData.name}" has been created.`,
					});
				}
				setIsModalOpen(false);
				setEditingTransaction(null);
				if (mode === "live") {
					const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
					await loadTransactions(sortParams, serverPageSize, cursor, columnFilterParams);
				} else {
					await loadTransactions();
				}
			} catch (err) {
				const message =
					err instanceof ApiError
						? err.message
						: "Failed to save recurring transaction";
				toast({ title: "Error", description: message, variant: "destructive" });
			} finally {
				setIsSaving(false);
			}
		} else {
			if (editingTransaction) {
				const updatedTransaction = editingTransaction.provenance === 'ai-suggested'
					? { ...transaction, provenance: 'manually-confirmed' as const }
					: transaction;
				setTransactions((prev) =>
					prev.map((t) => (t.id === updatedTransaction.id ? updatedTransaction : t)),
				);
			} else {
				setTransactions((prev) => [...prev, transaction]);
			}
			setIsModalOpen(false);
			setEditingTransaction(null);
		}
	};

	const formatAmount = (transaction: RecurringTransaction) => {
		if (transaction.isVariable) return "Variable";
		return formatCurrency(transaction.amount || 0, "full");
	};

	// ─── Column definitions ───────────────────────────────────────────────────

	const columns: ColumnConfig<RecurringTransaction>[] = useMemo(() => [
		{
			key: "name",
			label: "Name",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			width: 240,
			render: (row) => (
				<div className="flex items-center gap-2 flex-wrap">
					<span className="font-medium">{row.name}</span>
					{row.provenance === 'ai-suggested' && (
						<span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 border border-purple-200">
							AI-suggested
						</span>
					)}
					{row.provenance === 'manually-confirmed' && (
						<span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 border border-green-200">
							Manually confirmed
						</span>
					)}
				</div>
			),
		},
		{
			key: "amount",
			label: "Amount",
			sortable: true,
			filterable: true,
			filterConfig: { type: "number" },
			align: "right",
			width: 140,
			render: (row) => (
				<span className={`tabular-nums font-medium ${row.direction === 'inflow' ? 'text-success-600' : 'text-destructive'}`}>
					{formatAmount(row)}
				</span>
			),
		},
		{
			key: "frequency",
			label: "Frequency",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options: ["Daily", "Weekly", "Fortnightly", "Monthly", "Quarterly", "Annual"],
			},
			width: 130,
			render: (row) => row.frequency,
		},
		{
			key: "nextDueDate",
			label: "Next Due Date",
			sortable: true,
			filterable: true,
			filterConfig: { type: "date-range" },
			width: 140,
			render: (row) => format(row.nextDueDate, "d MMM yyyy"),
		},
		{
			key: "category",
			label: "Category",
			sortable: true,
			filterable: true,
			filterConfig: { type: "text" },
			width: 160,
			render: (row) => row.category,
		},
		{
			key: "status",
			label: "Status",
			sortable: true,
			filterable: true,
			filterConfig: {
				type: "select",
				options: ["Active", "Paused", "Ended"],
			},
			width: 110,
			render: (row) => (
				<Badge variant={getStatusVariant(row.status)}>
					{row.status}
				</Badge>
			),
		},
		{
			key: "id",
			label: "Actions",
			sortable: false,
			filterable: false,
			align: "right",
			width: 100,
			render: (row) => (
				<div
					className="flex items-center justify-end gap-1"
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					{canEdit ? (
						<>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8"
								onClick={() => handleEdit(row)}
							>
								<Pencil className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8"
								onClick={() => handleDelete(row)}
							>
								<Trash2 className="h-4 w-4 text-destructive" />
							</Button>
						</>
					) : (
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8"
							onClick={() => setViewingTransaction(row)}
						>
							<Eye className="h-4 w-4" />
						</Button>
					)}
				</div>
			),
		},
	], [canEdit, formatCurrency]);

	// ─── Render ───────────────────────────────────────────────────────────────

	return (
		<div className="flex flex-col h-full space-y-2 px-2 pt-0 pb-2 overflow-y-auto">
			{/* Scope guidance banner */}
			<div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
				<button
					type="button"
					className="flex w-full items-start gap-3 text-left"
					onClick={() => setBannerExpanded(prev => !prev)}
				>
					<Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
					<p className="flex-1 font-medium">
						This table is for transactions not already accounted for in your connected data sources.
					</p>
					<ChevronDown className={cn("mt-0.5 h-4 w-4 shrink-0 text-blue-500 transition-transform", bannerExpanded && "rotate-180")} />
				</button>
				{bannerExpanded && (
					<p className="mt-2 pl-7 text-blue-800 dark:text-blue-300">
						Recurring items already configured in your connected data sources — such as HP
						agreements, lease schedules, loan repayments, or standing journal
						templates — are brought across automatically and do not need to be entered here. Use this table for
						cash commitments that exist outside your connected systems, such as
						direct debits, standing orders, or manually managed obligations.
					</p>
				)}
			</div>

			{/* Error State */}
			{!isLoading && error && (
				<div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center">
					<p className="text-destructive font-medium">{error}</p>
					<Button
						variant="outline"
						size="sm"
						className="mt-3"
						onClick={loadTransactions}
					>
						Retry
					</Button>
				</div>
			)}

			{/* Maximus Table */}
			{!error && (
				<div className="min-h-[400px] flex-1 flex flex-col -mt-3">
				<MaximusTableReusable
					data={transactions}
					columns={columns}
					title=""
					isLoading={isLoading}
					searchPlaceholder="Search by name or category..."
					primaryAction={canEdit ? {
						label: "Add Recurring Transaction",
						icon: Plus,
						onClick: handleAdd,
					} : undefined}
					onSortChange={mode === "live" ? handleSortChange : undefined}
					onFilterChange={mode === "live" ? handleColumnFilterChange : undefined}
					pagination={{
						enabled: true,
						defaultPageSize: 25,
						pageSizeOptions: [25, 50, 100, 200],
						...(mode === "live"
							? {
									totalItems: serverTotalCount,
									controlledPage: serverPage,
									onPageChange: (page: number) => {
										setServerPage(page);
									},
									onPageSizeChange: (newSize: number) => {
										setServerPageSize(newSize);
										setServerPage(1);
										setPageCursors({});
									},
								}
							: {}),
					}}
					enableRowSelection={false}
					initialVisibleColumns={savedVisibleColumns}
					onExportToExcel={mode === "live" && hasPermission("export_data") ? triggerExport : undefined}
					onColumnConfigSave={(visibleKeys) => {
						const cols = visibleKeys.map((key, idx) => ({
							key,
							isVisible: true,
							order: idx,
						}));
						const allColumnKeys = columns.map((c) => c.key);
						const hiddenCols = allColumnKeys
							.filter((k) => !visibleKeys.includes(k))
							.map((key, idx) => ({
								key,
								isVisible: false,
								order: visibleKeys.length + idx,
							}));
						saveColumnPreferences([...cols, ...hiddenCols]);
					}}
					onSearchChange={mode === "live" ? handleSearchChange : undefined}
					onSearch={mode !== "live" ? (data, query) =>
						data.filter(
							(t) =>
								t.name.toLowerCase().includes(query.toLowerCase()) ||
								t.category.toLowerCase().includes(query.toLowerCase()),
						)
					 : undefined}
				/>
				</div>
			)}

			{/* Add/Edit Modal */}
			{isModalOpen && (
				<RecurringTransactionModal
					transaction={editingTransaction}
					onSave={handleSave}
					onClose={() => {
						setIsModalOpen(false);
						setEditingTransaction(null);
					}}
					isSaving={isSaving}
					mode={mode}
				/>
			)}

			{/* Delete Confirmation Modal */}
			{deletingTransaction && (
				<DeleteConfirmationModal
					transactionName={deletingTransaction.name}
					onConfirm={confirmDelete}
					onCancel={() => setDeletingTransaction(null)}
					isDeleting={isDeleting}
				/>
			)}

			{/* Read-only Detail Dialog */}
			<Dialog
				open={viewingTransaction !== null}
				onOpenChange={(open) => {
					if (!open) setViewingTransaction(null);
				}}
			>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>{viewingTransaction?.name}</DialogTitle>
					</DialogHeader>
					{viewingTransaction && (
						<div className="space-y-4 pt-2">
							<div className="rounded-lg border divide-y text-sm">
								<div className="flex justify-between px-4 py-3">
									<span className="text-muted-foreground">Amount</span>
									<span className="font-semibold">
										{formatAmount(viewingTransaction)}
									</span>
								</div>
								{viewingTransaction.direction && (
									<div className="flex justify-between px-4 py-3">
										<span className="text-muted-foreground">Direction</span>
										<span className="font-medium capitalize">
											{viewingTransaction.direction}
										</span>
									</div>
								)}
								<div className="flex justify-between px-4 py-3">
									<span className="text-muted-foreground">Frequency</span>
									<span className="font-medium">
										{viewingTransaction.frequency}
									</span>
								</div>
								<div className="flex justify-between px-4 py-3">
									<span className="text-muted-foreground">Next Due Date</span>
									<span className="font-medium">
										{format(viewingTransaction.nextDueDate, "d MMM yyyy")}
									</span>
								</div>
								<div className="flex justify-between px-4 py-3">
									<span className="text-muted-foreground">Category</span>
									<span className="font-medium">
										{viewingTransaction.category}
									</span>
								</div>
								{viewingTransaction.counterpartyName && (
									<div className="flex justify-between px-4 py-3">
										<span className="text-muted-foreground">Counterparty</span>
										<span className="font-medium">
											{viewingTransaction.counterpartyName}
										</span>
									</div>
								)}
								<div className="flex justify-between px-4 py-3">
									<span className="text-muted-foreground">Status</span>
									<Badge variant={getStatusVariant(viewingTransaction.status)}>
										{viewingTransaction.status}
									</Badge>
								</div>
								{viewingTransaction.notes && (
									<div className="px-4 py-3">
										<span className="text-muted-foreground block mb-1">Notes</span>
										<span className="text-sm">{viewingTransaction.notes}</span>
									</div>
								)}
								<div className="flex items-center justify-between px-4 py-3">
									<span className="text-muted-foreground">Source</span>
									<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
										{viewingTransaction.dataSourceName}
									</span>
								</div>
								{viewingTransaction.provenance && (
									<div className="flex items-center justify-between px-4 py-3">
										<span className="text-muted-foreground">Origin</span>
										{viewingTransaction.provenance === 'ai-suggested' && (
											<span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800 border border-purple-200">
												AI-suggested — review and confirm values
											</span>
										)}
										{viewingTransaction.provenance === 'manually-confirmed' && (
											<span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 border border-green-200">
												Manually confirmed
											</span>
										)}
									</div>
								)}
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
